import { Box, Button, Checkbox, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import FilterListIcon from '@mui/icons-material/FilterList';
import GetAppIcon from '@mui/icons-material/GetApp';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';
import type { Column } from 'src/types/table';
import { WAZUH_DATE_RANGE_OPTIONS } from 'src/mindtime/hooks/useWazuhScope';
import type {
  EndpointEventKpi,
  EndpointEventRow,
  EventSeverity,
  EventStatus,
} from '../endpointDetailsTypes';
import {
  COMPACT_TOOLBAR_FILTER_OVERRIDES,
  SecurityToolbarFilter,
  SecurityToolbarFilterPill,
} from '../../../shared/securityDashboardToolbar';

export const COLOR_BLUE = '#3182CE';
export const COLOR_RED = '#E53E3E';

export const SEVERITY_STYLES: Record<EventSeverity, { label: string; color: string; bg: string }> = {
  critical: { label: 'Critical', color: '#DC2626', bg: '#FEE2E2' },
  high: { label: 'High', color: '#EA580C', bg: '#FFEDD5' },
  medium: { label: 'Medium', color: '#CA8A04', bg: '#FEF9C3' },
  low: { label: 'Low', color: '#2563EB', bg: '#DBEAFE' },
  informational: { label: 'Informational', color: '#64748B', bg: '#F1F5F9' },
};

export const STATUS_LABELS: Record<EventStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
};

export const mutedCellStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#64748B',
};

export const DATE_RANGE_OPTIONS = [...WAZUH_DATE_RANGE_OPTIONS];

export const SeverityPill: FC<{ severity: EventSeverity }> = ({ severity }) => {
  const { label, color, bg } = SEVERITY_STYLES[severity];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: '10px',
        fontSize: 11,
        fontWeight: 600,
        color,
        backgroundColor: bg,
      }}
    >
      {label}
    </Box>
  );
};

export const toEventSeverity = (value?: string): EventSeverity => {
  const key = (value ?? '').trim().toLowerCase();
  if (
    key === 'critical' ||
    key === 'high' ||
    key === 'medium' ||
    key === 'low' ||
    key === 'informational'
  ) {
    return key;
  }
  return 'critical';
};

type EventFilterDropdownProps = {
  label: string;
  icon?: ReactNode;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
};

export const EventFilterDropdown: FC<EventFilterDropdownProps> = ({
  label,
  icon = <FilterListIcon style={{ fontSize: 16 }} />,
  options,
  selected,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const applied = selected.length > 0;
  const displayLabel = selected.length > 0 ? `${label} (${selected.length})` : label;

  const toggle = (option: string): void => {
    onChange(
      selected.includes(option)
        ? selected.filter((item) => item !== option)
        : [...selected, option]
    );
  };

  return (
    <>
      <SecurityToolbarFilterPill
        applied={applied}
        label={displayLabel}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        leadingIcon={icon}
        iconSize={16}
        textStyleOverrides={{ fontSize: 12, lineHeight: '18px' }}
        boxStyleOverrides={COMPACT_TOOLBAR_FILTER_OVERRIDES}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {options.map((option) => (
          <MenuItem
            key={option}
            onClick={() => toggle(option)}
            style={{ fontSize: 13, paddingTop: 2, paddingBottom: 2 }}
          >
            <Checkbox
              size="small"
              checked={selected.includes(option)}
              style={{ padding: 4, marginRight: 6 }}
            />
            {option}
          </MenuItem>
        ))}
        {selected.length > 0 ? (
          <MenuItem
            onClick={() => onChange([])}
            style={{ fontSize: 12, color: COLOR_BLUE, justifyContent: 'center' }}
          >
            Clear
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
};

export const DateRangeDropdown: FC<{ value: string; onChange: (next: string) => void }> = ({
  value,
  onChange,
}) => (
  <SecurityToolbarFilter
    compact
    label="Date Range"
    icon={<DateRangeIcon style={{ fontSize: 16 }} />}
    options={DATE_RANGE_OPTIONS.map((option) => ({ label: option, value: option }))}
    value={value}
    onChange={onChange}
  />
);

export type EndpointTableFilterField = {
  id: string;
  label: string;
  options: string[];
  icon?: ReactNode;
};

export const EVENT_TABLE_FILTER_IDS = ['severity', 'category', 'status', 'user', 'source'] as const;

export const EVENT_TABLE_FILTER_LABELS: Record<(typeof EVENT_TABLE_FILTER_IDS)[number], string> = {
  severity: 'Severity',
  category: 'Category',
  status: 'Status',
  user: 'User',
  source: 'Source',
};

export const createEmptyTableFilters = (fieldIds: readonly string[]): Record<string, string[]> =>
  Object.fromEntries(fieldIds.map((id) => [id, []]));

export const buildTableFilterFields = (
  labels: Record<string, string>,
  options: Record<string, string[]>
): EndpointTableFilterField[] =>
  Object.entries(labels).map(([id, label]) => ({
    id,
    label,
    options: options[id] ?? [],
  }));

type EndpointTableFiltersToolbarProps = {
  fields: EndpointTableFilterField[];
  selected: Record<string, string[]>;
  onChange: (fieldId: string, next: string[]) => void;
  onFilterApply?: () => void;
  dateRange?: string;
  onDateRangeChange?: (next: string) => void;
};

/** Shared filter toolbar for all endpoint detail table tabs. */
export const EndpointTableFiltersToolbar: FC<EndpointTableFiltersToolbarProps> = ({
  fields,
  selected,
  onChange,
  onFilterApply,
  dateRange,
  onDateRangeChange,
}) => {
  const handleFilterChange = (fieldId: string) => (next: string[]): void => {
    onChange(fieldId, next);
    onFilterApply?.();
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      flexWrap="wrap"
      style={{ gap: 8, padding: '12px 16px', borderBottom: '1px solid #E2E8F0' }}
    >
      {dateRange !== undefined && onDateRangeChange ? (
        <DateRangeDropdown value={dateRange} onChange={onDateRangeChange} />
      ) : null}
      {fields.map((field) => (
        <EventFilterDropdown
          key={field.id}
          label={field.label}
          icon={field.icon}
          options={field.options}
          selected={selected[field.id] ?? []}
          onChange={handleFilterChange(field.id)}
        />
      ))}
      <Button
        size="small"
        startIcon={<GetAppIcon style={{ fontSize: 16 }} />}
        style={{ textTransform: 'none', fontSize: 12, color: '#4A5568' }}
      >
        Export
      </Button>
    </Box>
  );
};

export const EndpointRowActionsMenu: FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      >
        <MoreVertIcon style={{ fontSize: 18, color: '#94A3B8' }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => setAnchorEl(null)} style={{ fontSize: 13 }}>
          View details
        </MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)} style={{ fontSize: 13 }}>
          Mark as resolved
        </MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)} style={{ fontSize: 13 }}>
          Create case
        </MenuItem>
      </Menu>
    </>
  );
};

export const TrendLabel: FC<{ value: string }> = ({ value }) => (
  <Box display="flex" alignItems="center" style={{ gap: 2, marginTop: 4 }}>
    <TrendingUpIcon style={{ fontSize: 12, color: COLOR_RED }} />
    <Typography style={{ fontSize: 11, fontWeight: 600, color: COLOR_RED }}>{value}</Typography>
  </Box>
);

export const KpiCell: FC<{ kpi: EndpointEventKpi; showDivider: boolean }> = ({
  kpi,
  showDivider,
}) => (
  <Box
    style={{
      minWidth: 0,
      padding: '0 20px',
      borderLeft: showDivider ? '1px solid #EDF2F7' : 'none',
    }}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between" style={{ marginBottom: 6 }}>
      <Typography
        style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', color: '#A0AEC0', textTransform: 'uppercase' }}
      >
        {kpi.label}
      </Typography>
      <ImportExportIcon style={{ fontSize: 16, color: COLOR_BLUE, flexShrink: 0 }} />
    </Box>
    <Typography style={{ fontSize: 26, fontWeight: 700, color: COLOR_RED, lineHeight: 1.1 }}>
      {String(kpi.value).padStart(2, '0')}
    </Typography>
    <TrendLabel value={kpi.trend} />
  </Box>
);

export const StatColumn: FC<{
  label: string;
  value: number;
  trend: string;
  showDivider: boolean;
}> = ({ label, value, trend, showDivider }) => (
  <Box
    style={{
      flex: '1 1 0',
      minWidth: 0,
      padding: '0 20px',
      borderLeft: showDivider ? '1px solid #EDF2F7' : 'none',
    }}
  >
    <Typography
      style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.4px', color: '#A0AEC0', textTransform: 'uppercase' }}
    >
      {label}
    </Typography>
    <Typography style={{ fontSize: 26, fontWeight: 700, color: COLOR_RED, lineHeight: 1.1, marginTop: 6 }}>
      {String(value).padStart(2, '0')}
    </Typography>
    <TrendLabel value={trend} />
  </Box>
);

/** Standard column set shared by the Events and Vulnerabilities tables. */
export const buildEventColumns = (): Column[] => [
  {
    id: 'name',
    label: 'Event Name',
    sortable: true,
    render: (row) => (
      <Typography style={{ fontSize: 12, fontWeight: 600, color: '#1E293B' }}>
        {(row as unknown as EndpointEventRow).name}
      </Typography>
    ),
  },
  {
    id: 'category',
    label: 'Category',
    render: (row) => <span style={mutedCellStyle}>{(row as unknown as EndpointEventRow).category}</span>,
  },
  {
    id: 'severity',
    label: 'Severity',
    render: (row) => <SeverityPill severity={(row as unknown as EndpointEventRow).severity} />,
  },
  {
    id: 'user',
    label: 'User',
    render: (row) => <span style={mutedCellStyle}>{(row as unknown as EndpointEventRow).user}</span>,
  },
  {
    id: 'source',
    label: 'Source',
    render: (row) => <span style={mutedCellStyle}>{(row as unknown as EndpointEventRow).source}</span>,
  },
  {
    id: 'time',
    label: 'Time',
    render: (row) => (
      <span style={{ ...mutedCellStyle, color: '#94A3B8' }}>
        {(row as unknown as EndpointEventRow).time}
      </span>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (row) => (
      <span style={{ fontSize: 12, color: '#1E293B' }}>
        {STATUS_LABELS[(row as unknown as EndpointEventRow).status]}
      </span>
    ),
  },
  {
    id: 'actions',
    label: 'Actions',
    align: 'right',
    render: () => (
      <Box style={{ textAlign: 'right' }}>
        <EndpointRowActionsMenu />
      </Box>
    ),
  },
];

/** Activity Timeline tab columns (Activity, Type, Performed By, etc.). */
export const buildActivityColumns = (): Column[] => [
  {
    id: 'name',
    label: 'Activity',
    sortable: true,
    render: (row) => (
      <Typography style={{ fontSize: 12, fontWeight: 600, color: '#1E293B' }}>
        {(row as unknown as EndpointEventRow).name}
      </Typography>
    ),
  },
  {
    id: 'category',
    label: 'Type',
    render: (row) => <span style={mutedCellStyle}>{(row as unknown as EndpointEventRow).category}</span>,
  },
  {
    id: 'severity',
    label: 'Severity',
    render: (row) => <SeverityPill severity={(row as unknown as EndpointEventRow).severity} />,
  },
  {
    id: 'user',
    label: 'Performed By',
    render: (row) => <span style={mutedCellStyle}>{(row as unknown as EndpointEventRow).user}</span>,
  },
  {
    id: 'source',
    label: 'Source',
    render: (row) => <span style={mutedCellStyle}>{(row as unknown as EndpointEventRow).source}</span>,
  },
  {
    id: 'time',
    label: 'Time',
    render: (row) => (
      <span style={{ ...mutedCellStyle, color: '#94A3B8' }}>
        {(row as unknown as EndpointEventRow).time}
      </span>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (row) => (
      <span style={{ fontSize: 12, color: '#1E293B' }}>
        {STATUS_LABELS[(row as unknown as EndpointEventRow).status]}
      </span>
    ),
  },
  {
    id: 'actions',
    label: 'Actions',
    align: 'right',
    render: () => (
      <Box style={{ textAlign: 'right' }}>
        <EndpointRowActionsMenu />
      </Box>
    ),
  },
];

type TablePaginationFooterProps = {
  page: number;
  pageCount: number;
  showingFrom: number;
  showingTo: number;
  total: number;
  onPageChange: (page: number) => void;
  itemLabel?: string;
};

export const TablePaginationFooter: FC<TablePaginationFooterProps> = ({
  page,
  pageCount,
  showingFrom,
  showingTo,
  total,
  onPageChange,
  itemLabel = 'assets',
}) => {
  const visibleCount = Math.min(pageCount, 3);
  const windowStart = Math.min(
    Math.max(0, page - Math.floor(visibleCount / 2)),
    Math.max(0, pageCount - visibleCount)
  );
  const pageIndexes = Array.from({ length: visibleCount }, (_, offset) => windowStart + offset);

  return (
    <>
      <Typography style={{ fontSize: 12, color: '#64748B' }}>
        Showing {showingFrom}-{showingTo} of {total.toLocaleString()} {itemLabel}
      </Typography>
      <Box display="flex" alignItems="center" style={{ gap: 6 }}>
        <IconButton
          size="small"
          disabled={page === 0}
          onClick={() => onPageChange(Math.max(0, page - 1))}
        >
          <KeyboardArrowDownIcon style={{ fontSize: 18, transform: 'rotate(90deg)', color: '#94A3B8' }} />
        </IconButton>
        {pageIndexes.map((pageIndex) => (
          <Box
            key={pageIndex}
            onClick={() => onPageChange(pageIndex)}
            style={{
              minWidth: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              color: page === pageIndex ? '#FFFFFF' : '#64748B',
              backgroundColor: page === pageIndex ? COLOR_BLUE : 'transparent',
            }}
          >
            {pageIndex + 1}
          </Box>
        ))}
        <IconButton
          size="small"
          disabled={page >= pageCount - 1}
          onClick={() => onPageChange(Math.min(pageCount - 1, page + 1))}
        >
          <KeyboardArrowDownIcon style={{ fontSize: 18, transform: 'rotate(-90deg)', color: '#94A3B8' }} />
        </IconButton>
      </Box>
    </>
  );
};
